import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useArtistStore } from "../../stores/artistStore.js";
import type { LpcLayers } from "../../infra/api.js";
import type { ArtistPersona, SonicTraits } from "../../domain/index.js";
import { Button } from "../../ui/Button.js";
import { AvatarBuilder } from "./AvatarBuilder.js";
import { Discography } from "./Discography.js";
import { PersonaEditor } from "./PersonaEditor.js";
import { SonicTraitsEditor } from "./SonicTraitsEditor.js";
import { ARTIST_NAME_MAX, isArtistValid, validateArtist } from "./artistValidation.js";

// F5: avatar builder edits these layers directly; LpcAvatar renders the
// preview inside AvatarBuilder. Defaults seed sensible starting layers
// for new artists.
const DEFAULT_LAYERS: LpcLayers = {
  body: { id: "light" },
  hair: { id: "short", color: "black" },
  shirt: { id: "tshirt", color: "white" },
  pants: { id: "jeans", color: "blue" },
};

interface Props {
  mode: "new" | "edit";
}

export function ArtistForm({ mode }: Props): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [layers, setLayers] = useState<LpcLayers>(DEFAULT_LAYERS);
  const [sonicTraits, setSonicTraits] = useState<SonicTraits>({});
  const [persona, setPersona] = useState<ArtistPersona>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Whether the user has tried to save once — until then we don't show
  // inline field errors so a blank new form doesn't look broken.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Seed the form from the cached/fetched artist exactly ONCE. Without this
  // guard the seeding effect re-ran on every artistStore change and clobbered
  // whatever the user was typing — that was the "navigation lock" / lost-state
  // bug: any store update snapped name+layers back to the saved values.
  const seeded = useRef(false);

  useEffect(() => {
    if (mode !== "edit" || !id || seeded.current) return;

    const seedFrom = (
      artist:
        | {
            name: string;
            lpc_layers: LpcLayers;
            sonic_traits?: SonicTraits;
            persona?: ArtistPersona;
          }
        | undefined,
    ): void => {
      if (!artist || seeded.current) return;
      seeded.current = true;
      setName(artist.name);
      setLayers(artist.lpc_layers);
      setSonicTraits(artist.sonic_traits ?? {});
      setPersona(artist.persona ?? {});
    };

    const existing = useArtistStore.getState().artists.find((a) => a.id === id);
    if (existing) {
      seedFrom(existing);
      return;
    }
    void useArtistStore
      .getState()
      .fetch()
      .then(() => {
        seedFrom(useArtistStore.getState().artists.find((a) => a.id === id));
      });
  }, [mode, id]);

  const fieldErrors = validateArtist(name, layers);
  const valid = isArtistValid(fieldErrors);

  async function onSave(): Promise<void> {
    setSubmitAttempted(true);
    if (!valid) {
      setError(null);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (mode === "new") {
        await useArtistStore.getState().create({
          name: name.trim(),
          lpc_layers: layers,
          sonic_traits: sonicTraits,
          persona,
        });
      } else if (id) {
        await useArtistStore.getState().update(id, {
          name: name.trim(),
          lpc_layers: layers,
          sonic_traits: sonicTraits,
          persona,
        });
      }
      nav("/artists");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(): Promise<void> {
    if (mode !== "edit" || !id) return;
    if (!confirm(`Apagar "${name}"? Ele será removido das bandas que ocupa.`)) return;
    try {
      await useArtistStore.getState().remove(id);
      nav("/artists");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const showNameError = submitAttempted && fieldErrors.name !== undefined;
  const showLayersError = submitAttempted && fieldErrors.layers !== undefined;

  return (
    <div className="flex flex-col gap-8">
      <header className="reveal flex items-end justify-between border-b border-border pb-6">
        <div>
          <p className="catalog-num mb-2">{mode === "new" ? "FICHA / NOVO" : "FICHA / EDITANDO"}</p>
          <h1
            className="font-display text-[52px] leading-[0.9] tracking-tight text-paper"
            style={{ fontVariationSettings: '"opsz" 144, "wght" 600, "SOFT" 100, "WONK" 1' }}
          >
            {mode === "new" ? (
              <>
                Novo <span className="italic text-butter">personagem</span>
              </>
            ) : (
              <>
                Ajustar <span className="italic text-butter">{name || "personagem"}</span>
              </>
            )}
          </h1>
        </div>
        <Button variant="ghost" onClick={() => nav("/artists")}>
          ← elenco
        </Button>
      </header>

      <div className="reveal-1 grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8">
          <div className="border border-border bg-tape p-6">
            <AvatarBuilder layers={layers} onChange={setLayers} label={name} />
          </div>
          {showLayersError && (
            <p
              role="alert"
              className="mt-2 border border-vinyl/40 bg-vinyl/5 p-3 font-mono text-xs uppercase tracking-widest2 text-vinyl"
            >
              {fieldErrors.layers}
            </p>
          )}
        </div>

        <div className="col-span-12 flex flex-col gap-4 lg:col-span-4">
          <div className="border border-border bg-tape p-5">
            <label className="block">
              <span className="catalog-num">NOME DO PERSONAGEM</span>
              <input
                className={`mt-2 w-full border-0 border-b bg-transparent px-0 py-2 font-display text-2xl text-paper outline-none ${
                  showNameError
                    ? "border-vinyl focus:border-vinyl"
                    : "border-border focus:border-paper"
                }`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="ex: Lia Vocal"
                maxLength={ARTIST_NAME_MAX}
                aria-invalid={showNameError}
                autoFocus
              />
            </label>
            {showNameError ? (
              <p
                role="alert"
                className="mt-2 font-mono text-[10px] uppercase tracking-widest2 text-vinyl"
              >
                {fieldErrors.name}
              </p>
            ) : (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-widest2 text-ink-dim tabular-nums">
                {String(name.trim().length)} / {String(ARTIST_NAME_MAX)}
              </p>
            )}
          </div>

          <div className="border border-border bg-tape p-5">
            <p className="catalog-num mb-1">IDENTIDADE SONORA</p>
            <p className="mb-4 font-mono text-[10px] leading-relaxed text-ink-dim">
              Como este personagem soa. A banda combina esses traços dos artistas dos slots e os
              injeta no prompt de cada música.
            </p>
            <SonicTraitsEditor value={sonicTraits} onChange={setSonicTraits} />
          </div>

          <div className="border border-border bg-tape p-5">
            <p className="catalog-num mb-1">PERSONA</p>
            <p className="mb-4 font-mono text-[10px] leading-relaxed text-ink-dim">
              O personagem por trás do som — uma bio editorial, o temperamento de performance e as
              influências declaradas. O temperamento e as influências entram no prompt; a bio não.
            </p>
            <PersonaEditor value={persona} onChange={setPersona} />
          </div>

          {error && (
            <p
              role="alert"
              className="border border-vinyl/40 bg-vinyl/5 p-3 font-mono text-xs uppercase tracking-widest2 text-vinyl"
            >
              {error}
            </p>
          )}

          <div className="flex flex-col gap-2">
            <Button
              variant="primary"
              onClick={() => void onSave()}
              disabled={saving || (submitAttempted && !valid)}
            >
              {saving ? "Salvando…" : mode === "new" ? "Cadastrar no elenco" : "Salvar mudanças"}
            </Button>
            {mode === "edit" && (
              <Button variant="danger" onClick={() => void onDelete()} disabled={saving}>
                Apagar personagem
              </Button>
            )}
          </div>
        </div>
      </div>

      {mode === "edit" && id && (
        <section className="reveal-2 flex flex-col gap-3">
          <p className="catalog-num">DISCOGRAFIA · TRAJETÓRIA DO PERSONAGEM</p>
          <p className="font-mono text-[10px] leading-relaxed text-ink-dim">
            Toda faixa de toda banda em que este personagem ocupa um slot, em ordem cronológica.
          </p>
          <Discography subject={{ kind: "artist", id }} />
        </section>
      )}
    </div>
  );
}
